import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Button,
  Grid,
  IconButton,
  Skeleton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import Page from 'src/components/Page';
import palette from 'src/theme/palette';
import { getInquiries, deleteInquiry, respondContact, getTourTypes } from 'src/ApiServices';
import { toast } from 'react-toastify';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import AddInquiryModal from 'src/sections/dashboard-sections/AddInquiryModal';
import ViewInquiryModal from 'src/sections/dashboard-sections/ViewInquiryModal';
import EditInquiryModal from 'src/sections/dashboard-sections/EditInquiryModal';
import InquirySettingsModal from 'src/sections/dashboard-sections/InquirySettingsModal';

export default function Inquiries() {
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [inquiryType, setInquiryType] = useState('all');
  const [pagination, setPagination] = useState({});
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [tourTypes, setTourTypes] = useState([]);

  const [appliedFilters, setAppliedFilters] = useState({
    search: '',
    type: 'all'
  });

  const fetchTourTypes = async () => {
    try {
      const response = await getTourTypes();
      setTourTypes(response.data || []);
    } catch (error) {
      console.error('Failed to load tour types', error);
      setTourTypes([]);
    }
  };

  const truncateText = (text, maxLength = 30) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const renderPropertyDetails = (inquiry) => {
    if (!inquiry.property) {
      return (
        <Typography
          sx={{
            color: palette.text.secondary,
            fontSize: '13px'
          }}
        >
          No Property Attached
        </Typography>
      );
    }

    const property = inquiry.property;

    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        {/* Property Image */}
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: 1,
            overflow: 'hidden',
            flexShrink: 0,
            backgroundColor: palette.grey[200]
          }}
        >
          {property?.main_image ? (
            <img
              src={property.main_image}
              alt={property.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: property.main_image ? 'none' : 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: palette.grey[300]
            }}
          >
            <Typography variant="caption" color="textSecondary">
              No Image
            </Typography>
          </Box>
        </Box>

        {/* Property Info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Tooltip title={property.title} arrow>
            <Typography
              sx={{
                fontFamily: 'poppinsMedium',
                fontSize: '14px',
                color: palette.text.primary,
                lineHeight: 1.2,
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {truncateText(property.title, 25)}
            </Typography>
          </Tooltip>

          <Tooltip title={property.address} arrow>
            <Typography
              sx={{
                fontSize: '12px',
                color: palette.text.secondary,
                lineHeight: 1.2,
                mb: 0.5,
                mt: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {truncateText(property.address, 30)}
            </Typography>
          </Tooltip>

          <Typography
            sx={{
              fontSize: '11px',
              color: palette.text.disabled,
              lineHeight: 1.2,
              mt: 1.45
            }}
          >
            {property.city || 'N/A'} | {property.state || 'N/A'}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Fixed search functionality
  const handleSearchSubmit = () => {
    setPage(1);
    const newFilters = {
      ...appliedFilters,
      search: searchQuery,
      type: inquiryType
    };
    setAppliedFilters(newFilters);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(1);
    const newFilters = {
      ...appliedFilters,
      search: ''
    };
    setAppliedFilters(newFilters);
  };

  const handleTypeChange = (event) => {
    const newType = event.target.value;
    setInquiryType(newType);
    setPage(1);
    const newFilters = {
      ...appliedFilters,
      type: newType
    };
    setAppliedFilters(newFilters);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleInquiryAction = (inquiry, action) => {
    setSelectedInquiry(inquiry);

    switch (action) {
      case 'view':
        setViewModalOpen(true);
        break;
      case 'add':
        setAddModalOpen(true);
        break;
      case 'edit':
        setEditModalOpen(true);
        break;
      case 'delete':
        setDeleteModalOpen(true);
        break;
      case 'settings':
        setSettingsModalOpen(true);
        break;
      default:
        break;
    }
  };

  const handleAddPropertyClick = () => {
    setAddModalOpen(true);
    setSelectedInquiry(null);
  };

  const handleCloseModal = () => {
    setViewModalOpen(false);
    setAddModalOpen(false);
    setEditModalOpen(false);
    setDeleteModalOpen(false);
    setSettingsModalOpen(false);
    setSelectedInquiry(null);
  };

  const handleModalSuccess = () => {
    handleCloseModal();
    fetchInquiriesData(page, appliedFilters);
  };

  const handleInquiryStatusChange = async (inquiryId, newStatus, message) => {
    try {
      await respondContact(inquiryId, {
        status: newStatus,
        response_message: message
      });
      fetchInquiriesData(page, appliedFilters);
    } catch (error) {
      console.error('Failed to update inquiry status:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedInquiry) return;

    try {
      setDeleteLoading(true);
      await deleteInquiry(selectedInquiry.id);
      toast.success('Inquiry deleted successfully!');
      fetchInquiriesData(page, appliedFilters);
      setDeleteModalOpen(false);
      setSelectedInquiry(null);
    } catch (error) {
      console.error('Failed to delete inquiry:', error);
      toast.error(error.response?.data?.message || 'Failed to delete inquiry!');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedInquiry(null);
  };

  const fetchInquiriesData = async (currentPage, filters = {}) => {
    setLoading(true);
    try {
      const params = {
        page: currentPage
      };

      if (filters.search && filters.search.trim() !== '') {
        params.search = filters.search.trim();
      }

      if (filters.type && filters.type !== 'all') {
        params.type = filters.type;
      }

      const response = await getInquiries(params);

      setInquiries(response.data || []);
      setPagination(
        response.pagination || {
          current_page: 1,
          per_page: 20,
          total: 0,
          last_page: 1,
          has_more_pages: false
        }
      );
    } catch (error) {
      console.error('Failed to load inquiries', error);
      setInquiries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTourTypes();
    fetchInquiriesData(page, appliedFilters);
  }, [page, appliedFilters]);

  const tableHeaders = [
    'Property Details',
    'Name',
    'Email',
    'Contact No.',
    'Date',
    'Time',
    'Type',
    'Status',
    'Actions'
  ];

  const stickyActionsColumnStyle = {
    px: 2,
    position: { xs: 'relative', md: 'sticky' },
    right: 0,
    width: 120,
    textAlign: 'center',
    zIndex: 1,
    backgroundColor: theme.palette.background.paper
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'confirmed':
        return 'success';
      case 'rejected':
        return 'error';
      case 'cancelled':
        return 'error';
      case 'pending':
        return 'warning';
      case 'completed':
        return 'info';
      default:
        return 'default';
    }
  };

  const renderSkeletonRows = () => {
    return Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={index}>
        {tableHeaders.map((_, cellIndex) => (
          <TableCell key={cellIndex} sx={{ px: 2 }}>
            <Skeleton variant="text" />
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  const renderInquiryRow = (inquiry) => {
    return (
      <>
        <TableCell sx={{ px: 2, minWidth: 200 }}>{renderPropertyDetails(inquiry)}</TableCell>
        <TableCell sx={{ px: 2 }}>{inquiry.name || 'N/A'}</TableCell>
        <TableCell sx={{ px: 2 }}>{inquiry.email || 'N/A'}</TableCell>
        <TableCell sx={{ px: 2 }}>{inquiry.contact_no || 'N/A'}</TableCell>
        <TableCell sx={{ px: 2 }}>{inquiry.date || 'N/A'}</TableCell>
        <TableCell sx={{ px: 2 }}>{inquiry.time ? inquiry.time.substring(0, 5) : 'N/A'}</TableCell>
        <TableCell sx={{ px: 2 }}>
          {inquiry.type?.name || inquiry.type?.replace('_', ' ') || 'N/A'}
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Box
            sx={{
              display: 'inline-block',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              fontSize: '12px',
              fontFamily: 'poppinsMedium',
              textTransform: 'capitalize'
            }}
          >
            {inquiry.status || 'pending'}
          </Box>
        </TableCell>
        <TableCell sx={stickyActionsColumnStyle}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
            <Tooltip title="Inquiry Settings">
              <IconButton
                size="small"
                onClick={() => handleInquiryAction(inquiry, 'settings')}
                sx={{
                  color: palette.info.main,
                  '&:hover': {
                    backgroundColor: palette.info.lighter
                  }
                }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Inquiry">
              <IconButton
                size="small"
                onClick={() => handleInquiryAction(inquiry, 'view')}
                sx={{
                  color: palette.primary.main,
                  '&:hover': {
                    backgroundColor: palette.primary.lighter
                  }
                }}
              >
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Inquiry">
              <IconButton
                size="small"
                onClick={() => handleInquiryAction(inquiry, 'edit')}
                sx={{
                  color: palette.warning.main,
                  '&:hover': {
                    backgroundColor: palette.warning.lighter
                  }
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Inquiry">
              <IconButton
                size="small"
                onClick={() => handleInquiryAction(inquiry, 'delete')}
                sx={{
                  color: palette.error.main,
                  '&:hover': {
                    backgroundColor: palette.error.lighter
                  }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </>
    );
  };

  return (
    <Page title="User Inquiries">
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            mt: 2,
            pb: 2,
            pl: 1
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 20, md: 25 },
              fontFamily: 'poppinsSemiBold',
              color: palette.primary.titledark
            }}
          >
            Tour request
          </Typography>

          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddPropertyClick}>
            Add New Inquiry
          </Button>
        </Box>

        {/* Search Section - Fixed with proper functionality */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={7} lg={8}>
            <TextField
              variant="outlined"
              placeholder="Search by name, email, contact no..."
              size="medium"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: palette.text.secondary }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch} edge="end">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                backgroundColor: theme.palette.background.paper,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: theme.palette.divider },
                  '&:hover fieldset': { borderColor: theme.palette.primary.main },
                  '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
                }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2} lg={2}>
            <FormControl fullWidth size="medium">
              <InputLabel id="inquiry-type-label">Inquiry Type</InputLabel>
              <Select
                labelId="inquiry-type-label"
                value={inquiryType}
                label="Inquiry Type"
                onChange={handleTypeChange}
                sx={{
                  fontFamily: 'poppinsMedium',
                  backgroundColor: theme.palette.background.paper
                }}
              >
                <MenuItem value="all">All</MenuItem>
                {tourTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={2}>
            <Button
              variant="contained"
              onClick={handleSearchSubmit}
              fullWidth
              sx={{
                height: 56,
                fontSize: '16px',
                fontFamily: 'poppinsMedium'
              }}
              startIcon={<SearchIcon />}
            >
              Search
            </Button>
          </Grid>
        </Grid>

        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer
              sx={{
                overflow: 'auto',
                '&::-webkit-scrollbar': { height: 8, width: 8 },
                '&::-webkit-scrollbar-track': { background: palette.grey[200] },
                '&::-webkit-scrollbar-thumb': {
                  background: palette.grey[400],
                  borderRadius: 2
                }
              }}
            >
              <Table
                sx={{
                  minWidth: 1400,
                  '& .MuiTableCell-head': {
                    backgroundColor: theme.palette.background.paper,
                    color: palette.primary.tableHeadTitle,
                    fontFamily: 'poppinsSemiBold',
                    fontSize: '14px',
                    borderBottom: `2px solid ${palette.divider}`,
                    py: 2
                  },
                  '& .MuiTableCell-body': {
                    color: palette.text.primary,
                    fontFamily: 'poppinsRegular',
                    fontSize: '14px',
                    borderBottom: `1px solid ${palette.divider}`,
                    py: 2
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((header, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          whiteSpace: 'nowrap',
                          px: 2,
                          position:
                            header === 'Actions' ? { xs: 'relative', md: 'sticky' } : 'relative',
                          backgroundColor: theme.palette.background.paper,
                          zIndex: header === 'Actions' ? 2 : 'auto',
                          ...(header === 'Actions' && stickyActionsColumnStyle)
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    renderSkeletonRows()
                  ) : inquiries.length > 0 ? (
                    inquiries.map((inquiry) => (
                      <TableRow
                        key={inquiry.id}
                        sx={{
                          '&:hover': { backgroundColor: palette.action.hover },
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        {renderInquiryRow(inquiry)}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={tableHeaders.length} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary">
                          {appliedFilters.search || appliedFilters.type !== 'all'
                            ? 'No inquiries match your search criteria'
                            : 'No inquiries found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {!loading && pagination.total > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
            <Pagination
              count={pagination.last_page}
              page={pagination.current_page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}

        <ViewInquiryModal
          open={viewModalOpen}
          onClose={handleCloseModal}
          inquiry={selectedInquiry}
          onSuccess={handleModalSuccess}
        />

        <AddInquiryModal
          open={addModalOpen}
          onClose={handleCloseModal}
          inquiry={selectedInquiry}
          onSuccess={handleModalSuccess}
        />

        <EditInquiryModal
          open={editModalOpen}
          onClose={handleCloseModal}
          inquiry={selectedInquiry}
          onSuccess={handleModalSuccess}
        />

        <InquirySettingsModal
          open={settingsModalOpen}
          onClose={() => {
            handleCloseModal();
            fetchInquiriesData(page, appliedFilters);
          }}
          inquiry={selectedInquiry}
          onStatusChange={handleInquiryStatusChange}
        />

        <Dialog
          open={deleteModalOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
          PaperProps={{
            sx: {
              borderRadius: 2,
              padding: 1
            }
          }}
        >
          <DialogTitle id="delete-dialog-title" sx={{ textAlign: 'center', py: 3 }}>
            <WarningAmberIcon
              sx={{
                fontSize: 60,
                color: palette.warning.main,
                mb: 2
              }}
            />
            <Typography
              variant="h6"
              sx={{ fontFamily: 'poppinsSemiBold', color: palette.text.primary }}
            >
              Confirm Deletion
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
            <DialogContentText
              id="delete-dialog-description"
              sx={{ color: palette.text.secondary, fontFamily: 'poppinsRegular' }}
            >
              Are you sure you want to delete inquiry from <strong>{selectedInquiry?.name}</strong>?
              <br />
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, px: 3 }}>
            <Button
              onClick={handleDeleteCancel}
              variant="outlined"
              sx={{
                borderColor: palette.grey[400],
                color: palette.text.primary,
                '&:hover': {
                  borderColor: palette.grey[600],
                  backgroundColor: palette.action.hover
                },
                minWidth: 100
              }}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              sx={{
                backgroundColor: palette.error.main,
                color: palette.error.contrastText,
                '&:hover': {
                  backgroundColor: palette.error.dark
                },
                minWidth: 100
              }}
              disabled={deleteLoading}
              startIcon={
                deleteLoading ? (
                  <Skeleton variant="circular" width={20} height={20} />
                ) : (
                  <DeleteIcon />
                )
              }
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Page>
  );
}
